import { type ForwardedRef, forwardRef } from 'react';
import { type FieldValues, type Path, type PathValue } from 'react-hook-form';

import { useFormElement } from '../useFormElement';

import { BaseCheckboxInput, type BaseCheckboxInputProps } from './BaseCheckboxInput';

export type CheckboxInputProps<T extends FieldValues, FP extends Path<T>> = Omit<
	BaseCheckboxInputProps,
	'hasError' | 'name' | 'onChange' | 'ref' | 'value'
> & {
	defaultValue?: PathValue<T, FP>;
	name: FP;
};

/**
 *
 * @param {CheckboxInputProps} props - The component's props
 * @param {string} label - label to display
 * @param {string} children - use this in case label is not enough
 * @param {string} defaultChecked - initial checked state
 * @param {string} disabled - is the checkbox disabled
 * @param {boolean} isOnError - can be used to activate the on-error style
 * @param {function} onChange - on box click change callback
 */
export const CheckboxInput = forwardRef(function CheckboxInput<T extends FieldValues, FP extends Path<T> = Path<T>>(
	{ defaultValue, name, ...props }: CheckboxInputProps<T, FP>,
	forwardedRef: ForwardedRef<HTMLDivElement>
) {
	const {
		field,
		fieldState: { error }
	} = useFormElement<T, FP>({
		defaultValue,
		name
	});

	return <BaseCheckboxInput {...field} error={error} {...props} ref={forwardedRef} />;
});
